import {
  DatePickerInput,
  LoadingSkeleton,
  AlertBanner,
  CustomButton,
  CustomCheckbox,
  CustomSelect,
  CustomTextarea,
  CustomTooltip,
} from '@/shared/ui/components';
import { helpIcon } from '@/shared/ui/assets';
import { DESCRIPTION_ENUM, OTHER_RMA_REASONS } from '@/shared/config';
import { getParentNode, withModalLayout } from '@/shared/lib';
import classNames from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import { t } from 'ttag';
import SearchSelectInput from './searchSelectInput';
import {
  getDescribeFieldCopy,
  maintenanceDeviceMessageRenderer,
} from '../../../lib/addRmaDeviceModal';
import { useAddRmaDeviceModal } from './useAddRmaDeviceModal';
import type { AddRMADeviceModalProps } from './addRmaDeviceModal.types';
import './addRmaDeviceModal.scss';

const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT: any = {};

const AddRMADeviceModal = withModalLayout((({
  handleUpdate,
  facilityId,
  isNotEditRMA,
  replacedDevices,
  toggleDisplayDrawer,
  selectedFacility,
  initialDeviceIssue = EMPTY_OBJECT,
  listCurrentDeviceIds = EMPTY_ARRAY,
  listCurrentReplacementDeviceIds,
  hasReplaceDevice = false,
  index,
  isLoading = false,
  hasProtectPlus,
  deviceIssueArray = EMPTY_ARRAY,
  onCancel,
  setTitle,
}: AddRMADeviceModalProps) => {
  const {
    state,
    deviceIssue,
    isEditing,
    errors,
    isMaintenanceDevice,
    onClickAddButton,
    loadOptionsDevices,
    onChangeDeviceId,
    onChangeReasonRMA,
    onChangePatientImpact,
    onChangeReasonDescribe,
    describeOptions,
    onChangeDateOfEvent,
    generateOptionReasonForRMA,
    replacedDeviceOptions,
    patientImpactList,
    deviceIdValue,
    setState,
  } = useAddRmaDeviceModal({
    handleUpdate,
    facilityId,
    replacedDevices,
    selectedFacility,
    initialDeviceIssue,
    listCurrentDeviceIds,
    listCurrentReplacementDeviceIds,
    index,
    hasProtectPlus,
    deviceIssueArray,
    onCancel,
    setTitle,
  });

  const describeOf = (reasonValue: string) =>
    _.find(deviceIssue?.reasons, (reason) => reason.value === reasonValue)?.describe;

  return (
    <div className="add-rma-device-body-modal">
      <div className="custom-modal-body-container">
        <AlertBanner
          alertType="warning"
          description={t`For issues related to the ECG of the Bioflux device, check the cable wires to make sure they are not loose prior to requesting an RMA for the device.`}
        />
        <div className="input-list">
          <div className="input-list-item w-100">
            <span>{t`Device ID`}</span>
          </div>
          <div className="input-list-input">
            <LoadingSkeleton isLoading={isLoading}>
              <SearchSelectInput
                disabled={isNotEditRMA}
                placeholder="Select device"
                name="deviceId"
                value={deviceIdValue}
                onChange={onChangeDeviceId}
                size="middle"
                errorMessage={errors.deviceId}
                isError={Boolean(errors.deviceId)}
                fetchOptions={loadOptionsDevices}
                isNumberOnly
                aria-label={t`Device ID`}
              />
            </LoadingSkeleton>
            {maintenanceDeviceMessageRenderer(
              isMaintenanceDevice,
              t`This device is marked as maintenance needed.`,
            )}
          </div>
        </div>
        {hasReplaceDevice && (
          <div className="input-list">
            <div className="input-list-item w-100">
              <span>{t`Replacement device ID`}</span>
            </div>
            <div className="input-list-input">
              <LoadingSkeleton isLoading={isLoading}>
                <CustomSelect
                  options={replacedDeviceOptions}
                  name="replacementDeviceId"
                  value={deviceIssue?.replacementDeviceId || replacedDeviceOptions[0]}
                  errorMessage={errors.replacementDeviceId}
                  isError={Boolean(errors.replacementDeviceId)}
                  size="middle"
                  onChange={onChangeDeviceId}
                  allowClear={false}
                  aria-label={t`Replacement device ID`}
                />
              </LoadingSkeleton>
            </div>
          </div>
        )}
        <div className="input-list">
          <div className="input-list-item w-100">
            <span>{t`Reason for RMA`}</span>
          </div>
          <div id="reason-rma-select" className={classNames('input-list-input')}>
            <LoadingSkeleton isLoading={isLoading}>
              <CustomSelect
                name="reasons"
                formatDropdownType="reasonRMA"
                mode={isMaintenanceDevice ? undefined : 'multiple'}
                value={_.map(deviceIssue?.reasons, (i) => i.value)}
                size="middle"
                onChange={onChangeReasonRMA}
                options={generateOptionReasonForRMA(deviceIssue?.deviceModel)}
                isError={Boolean(errors.reasons) || state.isWarning}
                errorMessage={(() => {
                  if (state.isWarning) {
                    return t`The RMA device limit has been reached.`;
                  }
                  return errors.reasons;
                })()}
                disabled={isNotEditRMA || _.isEmpty(deviceIssue?.deviceId) || isMaintenanceDevice}
                showSearch
                allowClear={false}
                placeholder={t`Select RMA reason`}
                aria-label={t`Reason for RMA`}
              />
            </LoadingSkeleton>
            <div className="reasons-description-wrap">
              {_.map(describeOptions, (option) => {
                const describeFieldLabel = getDescribeFieldCopy(option).label;
                return (
                  <div key={option.value}>
                    <div className="other-reasons-select-input-label">{describeFieldLabel}</div>
                    <LoadingSkeleton isLoading={isLoading}>
                      <CustomTextarea
                        name={option.value}
                        className={classNames(
                          option.value === DESCRIPTION_ENUM.OTHER && 'other-reasons-select-input',
                        )}
                        placeholder={describeFieldLabel}
                        aria-label={describeFieldLabel}
                        value={describeOf(option.value)}
                        onChange={(_name: unknown, value: string) =>
                          onChangeReasonDescribe(option.value, value)
                        }
                        isError={Boolean(errors.describes?.[option.value])}
                        errorMessage={errors.describes?.[option.value]}
                      />
                    </LoadingSkeleton>
                  </div>
                );
              })}
              {_.find(
                deviceIssue?.reasons || [],
                (x) => x.value === OTHER_RMA_REASONS.LOST_DEVICE,
              ) &&
                _.isNil(deviceIssue?.replacementDeviceId) && (
                  <div>
                    <LoadingSkeleton isLoading={isLoading}>
                      <div className="replacement-needed-checkbox">
                        <CustomCheckbox
                          checked={deviceIssue.lostDeviceNeedReplacement}
                          onChange={(checked) => {
                            onChangePatientImpact('lostDeviceNeedReplacement', checked);
                          }}
                          label={t`Receive a replacement device (fees may apply)`}
                          disabled={
                            isNotEditRMA ||
                            _.isEmpty(deviceIssue?.deviceId) ||
                            isMaintenanceDevice ||
                            !_.isNil(deviceIssue?.replacementDeviceId)
                          }
                        />
                      </div>
                    </LoadingSkeleton>
                  </div>
                )}
            </div>
            {maintenanceDeviceMessageRenderer(
              isMaintenanceDevice,
              t`This reason for RMA is default for maintenance device.`,
            )}
          </div>
        </div>
        <div className="input-list patient">
          <div className="input-list-item w-100">
            <span>
              {t`Patient impact`}
              <CustomTooltip
                title={t`Learn more about patient impact`}
                placement="bottom"
                getPopupContainer={(ele: any) => ele.parentNode.parentNode}
              >
                <div className="image-wrap" aria-hidden onClick={toggleDisplayDrawer}>
                  <img src={helpIcon} alt="icon help" />
                </div>
              </CustomTooltip>
            </span>
          </div>
          <div className="input-list-input">
            <LoadingSkeleton isLoading={isLoading}>
              <CustomSelect
                name="patientImpact"
                options={patientImpactList}
                disabled={isNotEditRMA || isMaintenanceDevice}
                value={deviceIssue?.patientImpact}
                size="middle"
                onChange={onChangePatientImpact}
                isError={Boolean(errors.patientImpact)}
                errorMessage={errors.patientImpact}
                showSearch
                allowClear={false}
                labelInValue
                aria-label={t`Patient impact`}
              />
            </LoadingSkeleton>
            {maintenanceDeviceMessageRenderer(
              isMaintenanceDevice,
              t`This patient impact is default for maintenance device.`,
            )}
          </div>
        </div>
        <div className="input-list">
          <div className="input-list-item w-100">
            <label htmlFor="date-of-event-input">{t`Date of event`}</label>
          </div>
          <div className="input-list-input">
            <LoadingSkeleton isLoading={isLoading}>
              <DatePickerInput
                id="date-of-event-input"
                className="date-of-event"
                name="dateOfEvent"
                placeholder="MM/DD/YYYY"
                value={deviceIssue?.dateOfEvent ? dayjs(deviceIssue.dateOfEvent) : null}
                aboveLimit={dayjs().endOf('day').valueOf()}
                onChange={onChangeDateOfEvent}
                getPopupContainer={(node: any) => getParentNode(node, 3)}
                isError={Boolean(errors.patientImpact)}
                errorMessage={errors.patientImpact}
              />
            </LoadingSkeleton>
          </div>
        </div>
      </div>
      <div className="custom-modal-footer-container">
        {!state.isEditing && (
          <LoadingSkeleton isLoading={isLoading}>
            <div className="keep-adding">
              <CustomCheckbox
                checked={state.isKeepAdding}
                onChange={(checked) => {
                  setState({ isKeepAdding: checked });
                }}
                label={t`Keep adding device`}
              />
            </div>
          </LoadingSkeleton>
        )}
        <LoadingSkeleton className="submit-btn-skeleton" isLoading={isLoading}>
          <div className="submit-btn">
            <CustomButton id="save-add-device-btn" onClick={onClickAddButton}>
              {isEditing ? t`Save` : t`Add Device`}
            </CustomButton>
          </div>
        </LoadingSkeleton>
      </div>
    </div>
  );
}) as any);

(AddRMADeviceModal as any).defaultProps = {
  width: 600,
  className: 'add-rma-device-modal',
};

export default AddRMADeviceModal;
